import React, { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { DebounceInput } from 'react-debounce-input'

import { httpOk, POST, GET } from 'helpers/api'

const TYPE_OPTIONS = [
	{ name: 'order_types', label: 'order_types' },
	{ name: 'user_role_types', label: 'user_role_types' },
	{ name: 'delivery_rules_types', label: 'delivery_rules_types' },
	{ name: 'employee_bonuses_types', label: 'employee_bonuses_types' },
	{ name: 'products_types', label: 'products_types' },
	{ name: 'products_system_types', label: 'products_system_types' },
	{ name: 'employee_salary_types', label: 'employee_salary_types' },
	{ name: 'loyalty_service_types', label: 'loyalty_service_types' },
	{ name: 'payment_purpose_types', label: 'payment_purpose_types' },
	{ name: 'auto_add_rules_types', label: 'auto_add_rules_types' },
	{ name: 'sms_types', label: 'sms_types' },
	{ name: 'permissions', label: 'permissions' },
	{ name: 'permissions_categories', label: 'permissions_categories' },
	{ name: 'counterparties', label: 'counterparties' },
	{ name: 'cheques_statuses_types', label: 'cheques_statuses_types' },
	{ name: 'movement_types', label: 'movement_types' },
	{ name: 'navigation', label: 'navigation' },
	{ name: 'users_brand_settings', label: 'users_brand_settings' },
]

function translationLanguageCode(language) {
	return language?.translation_code
		|| String(language?.code || '').replace(/-/g, '_').toLowerCase()
}

function extractEntityIdentifier(item) {
	const fields = ['value', 'code', 'permission', 'type', 'slug', 'key', 'transaction_type']
	for (const field of fields) {
		const raw = item?.[field]
		if (raw != null && String(raw).trim() !== '') {
			return String(raw).trim()
		}
	}
	return ''
}

function Index() {
	const { t } = useTranslation()

	const [languages, setLanguages] = useState([])
	const [selectedType, setSelectedType] = useState('')
	const [typeSearch, setTypeSearch] = useState('')
	const [rowSearch, setRowSearch] = useState('')
	const [onlyUntranslated, setOnlyUntranslated] = useState(false)
	const [isLoading, setIsLoading] = useState(false)

	const filteredTypes = useMemo(() => {
		const query = typeSearch.trim().toLowerCase()
		if (!query) return TYPE_OPTIONS
		return TYPE_OPTIONS.filter(item => item.name.toLowerCase().includes(query))
	}, [typeSearch])

	const baseRows = useMemo(() => {
		if (!languages.length || !languages[0]?.data?.length) return []
		return languages[0].data.map((item, rowIndex) => {
			const entityValue = item.entity_value
				|| extractEntityIdentifier(item)
				|| (item.id != null ? `#${item.id}` : '')

			return {
				rowIndex,
				id: item.id,
				name: item.name,
				entityValue,
			}
		})
	}, [languages])

	const filteredRowIndexes = useMemo(() => {
		const query = rowSearch.trim().toLowerCase()

		return baseRows
			.filter((row) => {
				if (onlyUntranslated) {
					const hasEmpty = languages.some(language => !String(language.data?.[row.rowIndex]?.value || '').trim())
					if (!hasEmpty) return false
				}

				if (!query) return true

				const haystack = [
					String(row.id),
					row.name,
					row.entityValue,
					...languages.map(language => language.data?.[row.rowIndex]?.value || ''),
				]
					.join(' ')
					.toLowerCase()

				return haystack.includes(query)
			})
			.map(row => row.rowIndex)
	}, [baseRows, languages, onlyUntranslated, rowSearch])

	async function createTranslation() {
		await POST('/admin/languages', {
			type: selectedType,
			languages,
		}, { loader: true })
	}

	function handleChange(languageIndex, rowIndex, value) {
		setLanguages(prev => {
			const next = [...prev]
			const languageCopy = { ...next[languageIndex] }
			const dataCopy = [...(languageCopy.data || [])]
			dataCopy[rowIndex] = {
				...dataCopy[rowIndex],
				value,
			}
			languageCopy.data = dataCopy
			next[languageIndex] = languageCopy
			return next
		})
	}

	async function getData(type) {
		setIsLoading(true)
		try {
			const response = await GET('/admin/db-types', { type }, { loader: true })
			if (!httpOk(response)) return

			setLanguages(prev => prev.map(language => {
				const languageCode = translationLanguageCode(language)
				const data = (response.data.data || []).map(item => {
					const translation = response.data.dataTranslations.find(
						row => row.entity_id === item.id && row.language === languageCode
					)
					const entityValue = extractEntityIdentifier(item)

					return {
						...item,
						entity_value: entityValue,
						value: translation?.value || '',
					}
				})

				return {
					...language,
					translation_code: languageCode,
					data,
				}
			}))

			setSelectedType(type)
			setRowSearch('')
			setOnlyUntranslated(false)
		} finally {
			setIsLoading(false)
		}
	}

	async function getLanguages() {
		const response = await GET('/admin/languages', {}, { loader: true })
		if (httpOk(response)) {
			setLanguages(response.data.map(language => ({
				...language,
				translation_code: translationLanguageCode(language),
			})))
		}
	}

	useEffect(() => {
		getLanguages()
	}, []) // eslint-disable-line react-hooks/exhaustive-deps

	return (
		<>
			<div className="page-header d-flex justify-content-between align-items-center">
				<div>
					<h4 className="vertical-center mb-1">{t('languages')}</h4>
					<p className="text-muted mb-0 fz-14">{t('languages_page_hint')}</p>
				</div>
				<button
					type="button"
					className="btn btn-primary"
					disabled={!selectedType || !languages.length}
					onClick={createTranslation}>
					<i className="uil uil-save me-1"></i>
					{t('save')}
				</button>
			</div>

			<div className="card lang-admin-card">
				<div className="card-body p-0">
					<div className="lang-admin-layout">
						<aside className="lang-admin-sidebar">
							<div className="lang-admin-sidebar-head">
								<div className="fw-semibold mb-2">{t('categories')}</div>
								<DebounceInput
									debounceTimeout={250}
									className="form-control form-control-sm"
									placeholder={t('search')}
									value={typeSearch}
									onChange={(e) => setTypeSearch(e.target.value)}
								/>
							</div>
							<div className="lang-admin-sidebar-list">
								{filteredTypes.map(item => (
									<button
										type="button"
										key={item.name}
										className={`lang-admin-type-btn ${item.name === selectedType ? 'active' : ''}`}
										onClick={() => getData(item.name)}>
										{item.label}
									</button>
								))}
							</div>
						</aside>

						<section className="lang-admin-main">
							<div className="lang-admin-toolbar">
								<div className="row g-2 align-items-center">
									<div className="col-md-6">
										<DebounceInput
											debounceTimeout={250}
											className="form-control"
											placeholder={t('languages_search_placeholder')}
											value={rowSearch}
											onChange={(e) => setRowSearch(e.target.value)}
										/>
									</div>
									<div className="col-md-6 d-flex align-items-center justify-content-md-end gap-3">
										<div className="form-check mb-0">
											<input
												id="only-untranslated"
												className="form-check-input"
												type="checkbox"
												checked={onlyUntranslated}
												onChange={(e) => setOnlyUntranslated(e.target.checked)}
											/>
											<label className="form-check-label" htmlFor="only-untranslated">
												{t('languages_only_untranslated')}
											</label>
										</div>
										<span className="text-muted fz-13 text-nowrap">
											{t('languages_rows_count', {
												shown: filteredRowIndexes.length,
												total: baseRows.length,
											})}
										</span>
									</div>
								</div>
								{selectedType && (
									<div className="mt-2 fz-13 text-muted">
										<b>{selectedType}</b>
									</div>
								)}
							</div>

							<div className="lang-admin-table-wrap">
								{!selectedType && (
									<div className="lang-admin-empty">{t('languages_select_type')}</div>
								)}

								{selectedType && isLoading && (
									<div className="lang-admin-empty">{t('loading')}...</div>
								)}

								{selectedType && !isLoading && !baseRows.length && (
									<div className="lang-admin-empty">{t('no_data')}</div>
								)}

								{selectedType && !isLoading && baseRows.length > 0 && (
									<table className="table table-sm lang-admin-table mb-0">
										<thead>
											<tr>
												<th className="lang-admin-col-key">#</th>
												<th className="lang-admin-col-key">{t('key')}</th>
												<th className="lang-admin-col-value">{t('value')}</th>
												{languages.map(language => (
													<th key={language.id || language.code}>
														<div className="fw-semibold">{language.name}</div>
														<div className="text-muted fw-normal fz-12">{language.code}</div>
													</th>
												))}
											</tr>
										</thead>
										<tbody>
											{filteredRowIndexes.map(rowIndex => {
												const row = baseRows.find(item => item.rowIndex === rowIndex)
												return (
													<tr key={row.id ?? rowIndex}>
														<td className="text-muted">{row.id}</td>
														<td className="lang-admin-key-cell">
															<div className="fw-600">{row.name}</div>
														</td>
														<td className="lang-admin-value-cell">
															<code className="lang-admin-code-badge">{row.entityValue || '—'}</code>
														</td>
														{languages.map((language, languageIndex) => (
															<td key={`${row.id}-${language.code}`}>
																<input
																	type="text"
																	className="form-control form-control-sm"
																	value={language.data?.[rowIndex]?.value ?? ''}
																	placeholder={row.name}
																	onChange={(e) => handleChange(languageIndex, rowIndex, e.target.value)}
																/>
															</td>
														))}
													</tr>
												)
											})}
										</tbody>
									</table>
								)}

								{selectedType && !isLoading && baseRows.length > 0 && filteredRowIndexes.length === 0 && (
									<div className="lang-admin-empty">{t('languages_nothing_found')}</div>
								)}
							</div>
						</section>
					</div>
				</div>
			</div>

			<style>{`
				.lang-admin-card { overflow: hidden; min-height: calc(100vh - 120px); }
				.lang-admin-layout { display: flex; min-height: calc(100vh - 120px); }
				.lang-admin-sidebar {
					width: 240px;
					flex-shrink: 0;
					border-right: 1px solid var(--dark-border, #eef2f7);
					background: var(--dark-surface-2, #fafbfc);
					display: flex;
					flex-direction: column;
				}
				.lang-admin-sidebar-head { padding: 16px; border-bottom: 1px solid var(--dark-border, #eef2f7); }
				.lang-admin-sidebar-list {
					padding: 8px;
					overflow: auto;
					flex: 1;
				}
				.lang-admin-type-btn {
					display: block;
					width: 100%;
					text-align: left;
					border: 0;
					background: transparent;
					padding: 10px 12px;
					border-radius: 8px;
					margin-bottom: 4px;
					font-size: 13px;
				}
				.lang-admin-type-btn:hover { background: var(--dark-surface-3, #eef2f7); }
				.lang-admin-type-btn.active {
					background: var(--dark-tint-primary, #e8f1ff);
					color: #1d4ed8;
					font-weight: 600;
				}
				.lang-admin-main {
					flex: 1;
					min-width: 0;
					display: flex;
					flex-direction: column;
				}
				.lang-admin-toolbar {
					padding: 16px;
					border-bottom: 1px solid var(--dark-border, #eef2f7);
					background: var(--dark-surface, #fff);
					position: sticky;
					top: 0;
					z-index: 2;
				}
				.lang-admin-table-wrap {
					flex: 1;
					overflow: auto;
					background: var(--dark-surface, #fff);
				}
				.lang-admin-table thead th {
					position: sticky;
					top: 0;
					z-index: 1;
					background: var(--dark-surface-2, #f8f9fb);
					vertical-align: top;
					border-bottom: 1px solid var(--dark-border, #dee2e6);
					box-shadow: inset 0 -1px 0 #dee2e6;
				}
				.lang-admin-col-key { width: 220px; min-width: 220px; }
				.lang-admin-col-value { width: 180px; min-width: 180px; }
				.lang-admin-key-cell { vertical-align: top; }
				.lang-admin-value-cell { vertical-align: top; }
				.lang-admin-code {
					font-size: 12px;
					color: var(--dark-text-muted, #6b7280);
					font-family: Consolas, monospace;
					margin-top: 2px;
					word-break: break-word;
				}
				.lang-admin-code-badge {
					display: inline-block;
					font-size: 12px;
					color: var(--dark-text, #374151);
					background: var(--dark-surface-2, #f3f4f6);
					border: 1px solid var(--dark-border, #e5e7eb);
					border-radius: 6px;
					padding: 4px 8px;
					word-break: break-word;
					white-space: normal;
				}
				.lang-admin-table td { vertical-align: top; }
				.lang-admin-table tbody tr:hover { background: var(--dark-surface-2, #fafbfc); }
				.lang-admin-empty {
					padding: 48px 24px;
					text-align: center;
					color: var(--dark-text-muted, #6b7280);
				}
			`}</style>
		</>
	)
}

export default Index
